import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TopBar } from '../components/layout/TopBar';
import { useAppStore, useAuthStore } from '../store/useStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { 
  Plus, 
  Clock, 
  Users, 
  Phone, 
  Bell,
  UserCheck,
  X,
  Crown,
  RefreshCw,
  ArrowRight,
  QrCode,
  Copy,
  ExternalLink,
  Smartphone,
  MessageSquare,
  CheckCircle,
  Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Waitlist() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { tables } = useAppStore();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedTableIds, setSelectedTableIds] = useState([]);
  const wsRef = useRef(null);

  const [formData, setFormData] = useState({
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    party_size: 2,
    priority: 'regular',
    notes: '',
    special_requests: '',
  });

  // QR Code URL for self-registration (points to production frontend, not backend)
  const PROD_URL = 'https://okreserva.okgo.mx';
  const qrUrl = `${PROD_URL}/waitlist-register/${user?.restaurant_id}`;
  const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrUrl)}`;

  const fetchWaitlist = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/waitlist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Error fetching waitlist:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!user?.restaurant_id) return;
    
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    wsRef.current = new WebSocket(`${wsUrl}/ws/${user.restaurant_id}`);
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'waitlist_updated') {
        fetchWaitlist();
        // Show notification for self-registered entries
        if (data.action === 'added' && data.data?.self_registered) {
          toast.success(`¡Nuevo cliente en lista! ${data.data.guest_name} (auto-registro QR)`);
        }
      }
    };
    
    wsRef.current.onerror = () => {
      // WebSocket error handled silently, reconnection managed by onclose
    };
    
    wsRef.current.onclose = () => {
      setTimeout(connectWebSocket, 3000);
    };
  }, [user?.restaurant_id, fetchWaitlist]);

  useEffect(() => {
    fetchWaitlist();
    connectWebSocket();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [fetchWaitlist, connectWebSocket]);

  const handleAddToWaitlist = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Cliente agregado a la lista de espera');
        setShowDialog(false);
        setFormData({
          guest_name: '',
          guest_phone: '',
          guest_email: '',
          party_size: 2,
          priority: 'regular',
          notes: '',
          special_requests: '',
        });
        fetchWaitlist();
      } else {
        toast.error('Error al agregar a la lista');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const handleNotify = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/waitlist/${entryId}/notify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`SMS enviado a ${data.phone}`, {
          description: 'El cliente ha sido notificado que su mesa está lista'
        });
        fetchWaitlist();
      }
    } catch (error) {
      toast.error('Error al notificar');
    }
  };

  const handleRemove = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/waitlist/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Cliente eliminado de la lista');
        fetchWaitlist();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleConvert = async () => {
    if (!selectedEntry || selectedTableIds.length === 0) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/waitlist/${selectedEntry.id}/convert?${selectedTableIds.map(id => `table_ids=${id}`).join('&')}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Cliente sentado exitosamente');
        setShowConvertDialog(false);
        setSelectedEntry(null);
        setSelectedTableIds([]);
        fetchWaitlist();
      }
    } catch (error) {
      toast.error('Error al sentar cliente');
    }
  };

  const openConvertDialog = (entry) => {
    setSelectedEntry(entry);
    setSelectedTableIds([]);
    setShowConvertDialog(true);
  };

  const copyQRLink = () => {
    navigator.clipboard.writeText(qrUrl);
    toast.success('Enlace copiado al portapapeles');
  };

  const availableTables = tables.filter(t => t.status === 'available' && (t.capacity_max || t.capacity) >= (selectedEntry?.party_size || 0));

  const priorityColors = {
    vip: 'bg-gradient-to-r from-amber-400 to-orange-500 text-white',
    regular: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    walk_in: 'bg-slate-500/10 text-slate-600 border-slate-500/30',
  };

  const statusColors = {
    waiting: 'bg-blue-500',
    notified: 'bg-amber-500',
    seated: 'bg-green-500',
    left: 'bg-red-500',
    converted: 'bg-teal-500',
  };

  const waitingEntries = entries.filter(e => e.status === 'waiting');
  const notifiedEntries = entries.filter(e => e.status === 'notified');

  return (
    <div className="min-h-screen" data-testid="waitlist-page">
      <TopBar 
        title="Lista de Espera" 
        helpSection="waitlist"
        actions={
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowQRDialog(true)}
              className="rounded-xl"
              data-testid="show-qr-btn"
            >
              <QrCode className="h-4 w-4 mr-1" /> Código QR
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchWaitlist}
              className="rounded-xl"
              data-testid="refresh-waitlist-btn"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
            <Button size="sm" onClick={() => setShowDialog(true)} className="rounded-xl" data-testid="add-waitlist-btn">
              <Plus className="h-4 w-4 mr-1" /> Agregar
            </Button>
          </div>
        }
      />

      <div className="p-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-blue-500/10">
                  <Clock className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{waitingEntries.length}</p>
                  <p className="text-xs text-muted-foreground">Esperando</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-amber-500/10">
                  <Bell className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{notifiedEntries.length}</p>
                  <p className="text-xs text-muted-foreground">Notificados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-yellow-500/10">
                  <Crown className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{entries.filter(e => e.priority === 'vip').length}</p>
                  <p className="text-xs text-muted-foreground">VIP</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{entries.reduce((sum, e) => sum + e.party_size, 0)}</p>
                  <p className="text-xs text-muted-foreground">Total personas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Self-registration QR prompt */}
        <Card className="mb-6 border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-primary/10">
                  <Smartphone className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Auto-registro con QR</p>
                  <p className="text-sm text-muted-foreground">
                    Los clientes pueden registrarse escaneando el código QR
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowQRDialog(true)} className="rounded-xl">
                <QrCode className="h-4 w-4 mr-1" /> Ver QR
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Waitlist */}
        <div className="space-y-3">
          {entries.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No hay clientes en lista de espera</p>
                <p className="text-sm mb-4">Agrega clientes manualmente o comparte el código QR</p>
                <div className="flex items-center justify-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowQRDialog(true)} className="rounded-xl">
                    <QrCode className="h-4 w-4 mr-1" /> Mostrar QR
                  </Button>
                  <Button size="sm" onClick={() => setShowDialog(true)} className="rounded-xl">
                    <Plus className="h-4 w-4 mr-1" /> Agregar manualmente
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            entries.map((entry, index) => (
              <Card 
                key={entry.id} 
                className={cn(
                  "border-0 shadow-sm transition-all hover:shadow-md",
                  entry.status === 'notified' && "ring-2 ring-amber-400"
                )}
                data-testid={`waitlist-entry-${entry.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Position */}
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg",
                        entry.priority === 'vip' ? priorityColors.vip : statusColors[entry.status]
                      )}>
                        {entry.priority === 'vip' ? <Crown className="h-5 w-5" /> : entry.position}
                      </div>

                      {/* Guest Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-lg">{entry.guest_name}</span>
                          {entry.self_registered && (
                            <Badge variant="outline" className="text-xs bg-primary/5">
                              <QrCode className="h-3 w-3 mr-1" /> Auto-registro
                            </Badge>
                          )}
                          {entry.status === 'notified' && (
                            <Badge className="bg-amber-500 text-white">
                              <Bell className="h-3 w-3 mr-1" /> Notificado
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" /> {entry.party_size} personas
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="h-3.5 w-3.5" /> {entry.guest_phone}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" /> ~{entry.estimated_wait_minutes} min
                          </span>
                        </div>
                        {entry.special_requests && (
                          <p className="text-sm text-muted-foreground mt-2 p-2 bg-muted rounded-lg">
                            "{entry.special_requests}"
                          </p>
                        )}
                        {entry.sms_notifications_sent > 0 && (
                          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {entry.sms_notifications_sent} SMS enviado(s)
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {entry.status === 'waiting' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleNotify(entry.id)}
                          className="rounded-xl"
                          data-testid={`notify-btn-${entry.id}`}
                        >
                          <Bell className="h-4 w-4 mr-1" /> Notificar SMS
                        </Button>
                      )}
                      {(entry.status === 'waiting' || entry.status === 'notified') && (
                        <Button 
                          variant="default" 
                          size="sm"
                          onClick={() => openConvertDialog(entry)}
                          className="rounded-xl"
                          data-testid={`seat-btn-${entry.id}`}
                        >
                          <UserCheck className="h-4 w-4 mr-1" /> Sentar
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleRemove(entry.id)}
                        className="rounded-xl text-destructive hover:text-destructive"
                        data-testid={`remove-btn-${entry.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Add to Waitlist Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="rounded-2xl" data-testid="waitlist-dialog">
          <DialogHeader>
            <DialogTitle>Agregar a Lista de Espera</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.guest_name}
                onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                placeholder="Nombre completo"
                className="rounded-xl"
                data-testid="waitlist-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono *</Label>
                <Input
                  value={formData.guest_phone}
                  onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                  placeholder="+52 55 1234 5678"
                  className="rounded-xl"
                  data-testid="waitlist-phone-input"
                />
              </div>
              <div>
                <Label>Personas *</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.party_size}
                  onChange={(e) => setFormData({ ...formData, party_size: parseInt(e.target.value) || 1 })}
                  className="rounded-xl"
                  data-testid="waitlist-party-size-input"
                />
              </div>
            </div>

            <div>
              <Label>Email (opcional)</Label>
              <Input
                type="email"
                value={formData.guest_email}
                onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                placeholder="correo@ejemplo.com"
                className="rounded-xl"
              />
            </div>

            <div>
              <Label>Prioridad</Label>
              <Select 
                value={formData.priority} 
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger className="rounded-xl" data-testid="waitlist-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vip">⭐ VIP</SelectItem>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="walk_in">Walk-in</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Peticiones especiales</Label>
              <Textarea
                value={formData.special_requests}
                onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                placeholder="Silla alta, cumpleaños, preferencia de zona..."
                rows={2}
                className="rounded-xl"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button 
              onClick={handleAddToWaitlist} 
              disabled={!formData.guest_name || !formData.guest_phone}
              className="rounded-xl"
              data-testid="submit-waitlist-btn"
            >
              Agregar a lista
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-primary" />
              Código QR para Auto-registro
            </DialogTitle>
          </DialogHeader>

          <div className="text-center space-y-4">
            <div className="bg-white p-4 rounded-2xl inline-block mx-auto">
              <img 
                src={qrApiUrl} 
                alt="QR Code para lista de espera"
                className="w-64 h-64"
              />
            </div>
            
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Los clientes pueden escanear este código para registrarse en la lista de espera
              </p>
              <div className="flex items-center gap-2 p-3 bg-muted rounded-xl">
                <input 
                  type="text" 
                  value={qrUrl} 
                  readOnly 
                  className="flex-1 bg-transparent text-sm truncate border-0 outline-none"
                />
                <Button variant="ghost" size="sm" onClick={copyQRLink} className="rounded-lg">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button variant="outline" onClick={copyQRLink} className="rounded-xl">
                <Copy className="h-4 w-4 mr-2" /> Copiar enlace
              </Button>
              <Button variant="outline" onClick={() => window.open(qrUrl, '_blank')} className="rounded-xl">
                <ExternalLink className="h-4 w-4 mr-2" /> Abrir página
              </Button>
            </div>

            <div className="p-4 bg-primary/5 rounded-xl text-left">
              <p className="text-sm font-medium text-primary mb-2">¿Cómo funciona?</p>
              <ol className="text-sm text-muted-foreground space-y-1">
                <li>1. El cliente escanea el QR con su teléfono</li>
                <li>2. Ingresa su nombre, teléfono y número de personas</li>
                <li>3. Aparece automáticamente en tu lista de espera</li>
                <li>4. Recibirá un SMS cuando su mesa esté lista</li>
              </ol>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowQRDialog(false)} className="w-full rounded-xl">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Reservation Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="rounded-2xl" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle>Sentar Cliente</DialogTitle>
          </DialogHeader>

          {selectedEntry && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-muted">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">{selectedEntry.guest_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedEntry.party_size} personas</p>
                  </div>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Seleccionar Mesa(s)</Label>
                <div className="grid grid-cols-4 gap-2">
                  {availableTables.map((table) => (
                    <Button
                      key={table.id}
                      type="button"
                      variant={selectedTableIds.includes(table.id) ? 'default' : 'outline'}
                      size="sm"
                      className="rounded-xl"
                      onClick={() => {
                        if (selectedTableIds.includes(table.id)) {
                          setSelectedTableIds(selectedTableIds.filter(id => id !== table.id));
                        } else {
                          setSelectedTableIds([...selectedTableIds, table.id]);
                        }
                      }}
                      data-testid={`select-table-${table.id}`}
                    >
                      {table.number || table.name} ({table.capacity_max || table.capacity}p)
                    </Button>
                  ))}
                </div>
                {availableTables.length === 0 && (
                  <p className="text-sm text-destructive mt-2 p-3 bg-destructive/10 rounded-xl">
                    No hay mesas disponibles para {selectedEntry.party_size} personas
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button 
              onClick={handleConvert} 
              disabled={selectedTableIds.length === 0}
              className="rounded-xl"
              data-testid="confirm-seat-btn"
            >
              <CheckCircle className="h-4 w-4 mr-1" /> Confirmar y sentar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
